<template>
  <b-container fluid>
    <!-- API Stats Page Banner -->
    <page-banner>
      <b-col class="mb-sm-4" sm>
        <h1>{{ $t('api_stats.page_title') }}</h1>
      </b-col>

      <b-col class="mb-4" sm>
        <v-select
          :dir="isLangRTL(locale) ? 'rtl' : 'ltr'"
          v-model="tableView"
          :clearable="false"
          class="w-100 styled-select ml-auto"
          :options="tableViewOptions"
          label="name">
          <template slot="option" slot-scope="option">
            <div class="ml-2 rtl-mr-2 dropdown-option">
              {{ option.name }}
            </div>
          </template>
          <template slot="selected-option" slot-scope="option">
            <div class="ml-2 rtl-mr-2 dropdown-option">
              {{ option.name }}
            </div>
          </template>
        </v-select>
      </b-col>

      <b-col cols="12">
        <div class="bg-white rounded-lg py-5 px-5">
          <b-row>
            <b-col class="mb-3 mb-lg-0" cols="12" lg="4">
              <stats-card :title="$t('api_stats.cumulative_cards.hits')" icon="profile-duo" :value="cumulativeData?.hits" />
            </b-col>
            <b-col class="mb-3 mb-lg-0" cols="12" lg="4">
              <stats-card :title="$t('api_stats.cumulative_cards.accounts')" icon="accounts" :value="cumulativeData?.applications" />
            </b-col>
            <b-col class="mb-3 mb-lg-0" cols="12" lg="4">
              <stats-card :title="$t('api_stats.cumulative_cards.est_impacted_users')" icon="speedometer" :value="cumulativeData?.estimatedUsers" />
            </b-col>
          </b-row>
        </div>
      </b-col>
    </page-banner>

    <!-- Table Filters -->
    <b-row class="pb-2 px-4 pt-4 bg-white" align-v="center">
      <b-col cols="12" sm="5" md="4" lg="3">
        <label for="datepicker-min">{{ $t('api_stats.start_date') }}</label>
        <b-form-datepicker id="datepicker-min" v-model="fromDate" :max="toDate" class="mb-2"></b-form-datepicker>
      </b-col>
      <b-col cols="12" sm="5" md="4" lg="3">
        <label for="datepicker-max">{{ $t('api_stats.end_date') }}</label>
        <b-form-datepicker id="datepicker-max" v-model="toDate" :disabled="!fromDate" :min="fromDate" :max="today" class="mb-2"></b-form-datepicker>
      </b-col>
      <b-col class="ml-auto" cols="12" sm="2">
        <b-button size="sm" variant="dark" class="float-right" @click="downloadStatsCSV">
          {{ $t('api_stats.download_data') }}
        </b-button>
      </b-col>
    </b-row>

    <!-- The API Stats Table -->
    <b-row class="pb-4 px-4 pt-3 bg-white">
      <b-col>
        <APIApplicationsTable v-if="tableView.value === 'applications'" :fromDate="fromDate" :toDate="toDate" />
        <APIEndpointsTable v-if="tableView.value === 'endpoints'" :fromDate="fromDate" :toDate="toDate" />
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import axios from 'axios'
import querystring from 'querystring'
import { download } from '~/utils/export'
import { mapGetters } from 'vuex'
import PageBanner from '~/components/PageBanner'
import StatsCard from '~/pages/api_stats/components/StatsCard'
import APIApplicationsTable from '~/pages/api_stats/components/APIApplicationsTable'
import APIEndpointsTable from '~/pages/api_stats/components/APIEndpointsTable'
import { formatDate } from '~/utils/time'

export default {
  components: {
    PageBanner,
    StatsCard,
    APIApplicationsTable,
    APIEndpointsTable
  },
  mounted () {
    this.fetchCumulativeUsageStats()
  },
  methods: {
    async fetchCumulativeUsageStats () {
      this.isLoading = true

      try {
        await this.$store.dispatch('usage/fetchCumulativeUsage')
      } catch (e) {
        this.$noty.error(this.$t('error_alert_text'))
      } finally {
        this.isLoading = false
      }
    },
    async downloadStatsCSV () {
      const queryOptions = {
        fromDate: this.fromDate,
        toDate: this.toDate
      }

      try {
        // Download request must be authenticated
        const { data: responseArrayBuffer } = await axios.get(`/api/usage/export/${this.tableView.value}?${querystring.stringify(queryOptions)}`, {
          headers: {
            Accept: 'text/csv; charset=utf-8',
            'Content-Type': 'text/csv; charset=utf-8'
          },
          responseType: 'arraybuffer'
        })

        const data = new File([responseArrayBuffer], 'export', { type: 'text/csv' })

        // https://developer.mozilla.org/en-US/docs/Web/API/URL/createObjectURL
        const url = window.URL.createObjectURL(data)

        download(url)

        window.URL.revokeObjectURL(url)

        this.$fireGTEvent(this.$gtagEvents.DownloadAPIReport)
      } catch (e) {
        this.$noty.error(this.$t('error_alert_text'))
      }
    }
  },
  computed: {
    ...mapGetters({
      cumulativeData: 'usage/currentCumulativeData',
      locale: 'lang/locale'
    })
  },
  data () {
    return {
      isLoading: true,
      // First day of the current month
      fromDate: formatDate(new Date((new Date()).getFullYear(), (new Date()).getMonth(), 1)),
      // Today
      toDate: formatDate(new Date()),
      today: formatDate(new Date()),
      tableView: {
        name: this.$t('api_stats.table_select.keys'),
        value: 'applications'
      },
      tableViewOptions: [
        {
          name: this.$t('api_stats.table_select.keys'),
          value: 'applications'
        },
        {
          name: this.$t('api_stats.table_select.routes'),
          value: 'endpoints'
        }]
    }
  }
}
</script>
